import React, { useCallback, useEffect, useState } from 'react';
import { Eye, Pencil, Search, X } from 'lucide-react';
import { GovernanceBreadcrumbs, StatusBadge, btnPrimary, btnSecondary, inputClass, selectClass } from '../components/shared';
import { aiGovernanceService, AssignmentItem, PolicyListItem } from '../../../../components/api/aiGovernanceService';

export default function AssignmentsScreen() {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [rows, setRows] = useState<AssignmentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAssign, setShowAssign] = useState(false);
  const [policies, setPolicies] = useState<PolicyListItem[]>([]);
  const [form, setForm] = useState({
    resourceId: '',
    resourceName: '',
    resourceType: 'Chatbot',
    policyId: '',
    departmentName: '',
    inheritanceSource: 'Enterprise Default',
  });
  const [saving, setSaving] = useState(false);

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await aiGovernanceService.getAssignments({
        search: search || undefined,
        resourceType: typeFilter !== 'All' ? typeFilter : undefined,
        page: 1,
        pageSize: 100,
      });
      setRows(res.items || []);
    } catch {
      setError('Failed to load assignments.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  useEffect(() => {
    (async () => {
      try {
        const res = await aiGovernanceService.getPolicies({ page: 1, pageSize: 100, status: 'Active' });
        setPolicies(res.items || []);
      } catch {
        // ignore
      }
    })();
  }, []);

  const resourceTypes = Array.from(new Set(rows.map((a) => a.resourceType).filter(Boolean)));

  const handleAssign = async () => {
    if (!form.resourceId || !form.policyId) {
      setError('Resource ID and policy are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await aiGovernanceService.createAssignment({
        ...form,
        resourceName: form.resourceName || form.resourceId,
      });
      setShowAssign(false);
      setForm({ resourceId: '', resourceName: '', resourceType: 'Chatbot', policyId: '', departmentName: '', inheritanceSource: 'Enterprise Default' });
      await fetchAssignments();
    } catch {
      setError('Failed to create assignment. Resource may already be assigned.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <GovernanceBreadcrumbs items={[{ label: 'AI Governance' }, { label: 'Assignments' }]} />

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Policy Assignments</h1>
          <p className="text-xs text-slate-500 mt-0.5">View where policies are applied across resources with inheritance tracking.</p>
        </div>
        <button onClick={() => setShowAssign(true)} className={btnSecondary}>Assign Policy</button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">{error}</div>}

      <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search resources..." className={`${inputClass} pl-8`} />
        </div>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={`${selectClass} w-40`}>
          <option value="All">Resource Type</option>
          {resourceTypes.map((t) => <option key={t}>{t}</option>)}
          {!resourceTypes.includes('Chatbot') && <option>Chatbot</option>}
          {!resourceTypes.includes('Workflow') && <option>Workflow</option>}
          {!resourceTypes.includes('LLM') && <option>LLM</option>}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {['Resource Name', 'Resource Type', 'Department', 'Assigned Policy', 'Inheritance Source', 'Override Status', 'Last Evaluated', 'Actions'].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">No assignments found.</td></tr>
            ) : rows.map((row) => (
              <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50/80">
                <td className="px-3 py-2 font-medium text-slate-800">{row.resourceName}</td>
                <td className="px-3 py-2 text-slate-600">{row.resourceType}</td>
                <td className="px-3 py-2 text-slate-600">{row.department || '—'}</td>
                <td className="px-3 py-2 text-indigo-700 font-medium">{row.assignedPolicy}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                    row.inheritanceSource === 'Enterprise Default' ? 'bg-slate-100 text-slate-600' :
                    row.inheritanceSource === 'Department Default' ? 'bg-blue-50 text-blue-700' :
                    'bg-purple-50 text-purple-700'
                  }`}>{row.inheritanceSource}</span>
                </td>
                <td className="px-3 py-2"><StatusBadge status={row.overrideStatus} /></td>
                <td className="px-3 py-2 text-slate-500 font-mono text-[10px]">{row.lastEvaluated}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    <button className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-indigo-600"><Eye className="w-3.5 h-3.5" /></button>
                    <button className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-indigo-600"><Pencil className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAssign && (
        <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-900">Assign Policy</h3>
              <button onClick={() => setShowAssign(false)} className="p-1 rounded hover:bg-slate-100 text-slate-400"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">Resource ID</label>
                <input value={form.resourceId} onChange={(e) => setForm({ ...form, resourceId: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">Resource Name</label>
                <input value={form.resourceName} onChange={(e) => setForm({ ...form, resourceName: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">Resource Type</label>
                <select value={form.resourceType} onChange={(e) => setForm({ ...form, resourceType: e.target.value })} className={selectClass}>
                  <option>Chatbot</option><option>Workflow</option><option>Knowledge Base</option><option>LLM</option><option>Data Connection</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">Policy</label>
                <select value={form.policyId} onChange={(e) => setForm({ ...form, policyId: e.target.value })} className={selectClass}>
                  <option value="">Select policy</option>
                  {policies.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">Department</label>
                <input value={form.departmentName} onChange={(e) => setForm({ ...form, departmentName: e.target.value })} className={inputClass} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowAssign(false)} className={btnSecondary}>Cancel</button>
              <button onClick={handleAssign} disabled={saving} className={btnPrimary}>{saving ? 'Saving...' : 'Assign'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
